import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { isCancelledError } from '@tanstack/react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { AnalyticsSession } from 'js/analytics_v2/session';
import { ReaderCategory } from 'js/analytics_v2/types';
import { analyticsPage } from 'js/analytics_v2/routes';
import {
  analyticsPreviewFromUrl,
  withAnalyticsPreview,
  withAnalyticsLandingContext,
} from 'js/analytics_v2/options';
import { LegacyAnalyticsContext } from 'js/analytics/AnalyticsExperienceBoundary';
import {
  AnalyticsUpgradeRequired,
  fetchReaderTotalCount,
} from 'js/analytics_v2/api';
import { Body } from 'js/design_system/Body';
import {
  AnalyticsTrackingContext,
  analyticsClick,
  analyticsClickDetails,
} from 'js/analytics_v2/tracking';

export const AnalyticsSessionContext = createContext<AnalyticsSession | null>(
  null,
);

export const useAnalyticsSession = (): AnalyticsSession => {
  const session = useContext(AnalyticsSessionContext);
  if (!session) throw new Error('Analytics requires its app session');
  return session;
};

export const readerCategory = (search: string): ReaderCategory => {
  const category = new URLSearchParams(search).get('category');
  return category === 'highly_engaged' ||
    category === 'in_network' ||
    category === 'in_field'
    ? category
    : 'all';
};

export const useAnalyticsSearch = (): {
  search: string;
  select: (name: string, value: string, remove?: string[]) => void;
} => {
  const history = useHistory();
  const { search, pathname } = useLocation();
  const session = useAnalyticsSession();
  session.rememberSearch(pathname, search);
  return {
    search,
    select: (name, value, remove = []) => {
      const params = new URLSearchParams(search);
      params.set(name, value);
      remove.forEach((key) => params.delete(key));
      history.push({ search: params.toString() });
    },
  };
};

export const useAnalyticsHref = (): ((href: string) => string) => {
  const session = useContext(AnalyticsSessionContext);
  const legacy = useContext(LegacyAnalyticsContext);
  return (href) =>
    session
      ? session.href(href)
      : withAnalyticsLandingContext(
          withAnalyticsPreview(
            href,
            legacy?.analytics_preview ?? analyticsPreviewFromUrl(),
          ),
          legacy,
        );
};

export const AnalyticsNavigation: React.FC<{
  analyticsBasePath: string;
  children: React.ReactNode;
}> = ({ analyticsBasePath, children }) => {
  const history = useHistory();
  const location = useLocation();
  const session = useAnalyticsSession();
  const { tracking, routeTracking } = useContext(AnalyticsTrackingContext);
  const pending = useRef<AbortController>();
  const [checking, setChecking] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setChecking(false);
    return () => pending.current?.abort();
  }, [session, location]);

  const navigate = async (url: URL): Promise<void> => {
    const controller = new AbortController();
    pending.current = controller;
    setChecking(true);
    try {
      await fetchReaderTotalCount(
        session.context.viewed_user_id,
        true,
        session.options,
        undefined,
        session.request(controller.signal, { replaceHistory: false }),
      );
      if (!controller.signal.aborted)
        history.push(`${url.pathname}${url.search}${url.hash}`);
    } catch (error) {
      if (
        !controller.signal.aborted &&
        !isCancelledError(error) &&
        !(error instanceof AnalyticsUpgradeRequired)
      )
        setFailed(true);
    } finally {
      if (pending.current === controller) setChecking(false);
    }
  };

  const click = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    const anchor = (event.target as Element).closest('a');
    if (
      !anchor ||
      anchor.hasAttribute('download') ||
      (anchor.target && anchor.target !== '_self')
    )
      return;
    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    const page = analyticsPage(url.pathname, analyticsBasePath);
    if (!page) return;
    event.preventDefault();
    pending.current?.abort();
    setFailed(false);
    if (session.context.access_mode === 'non_premium' && page !== 'overview') {
      void navigate(url);
      return;
    }
    history.push(`${url.pathname}${url.search}${url.hash}`);
    if (page !== 'reader_profiles' || !url.searchParams.has('reader'))
      window.scrollTo(0, 0);
  };

  const track = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.button > 1) return;
    const details = analyticsClickDetails(
      event.target as Element,
      analyticsBasePath,
      session.context,
    );
    if (!details || !tracking) return;
    const send = (): void =>
      analyticsClick(
        tracking,
        session.context,
        details.filter
          ? 'analytics_dashboard_filter'
          : 'analytics_dashboard_click',
        details,
      );
    if (routeTracking?.current) routeTracking.current.click(send);
    else send();
  };
  return (
    <div
      aria-busy={checking || undefined}
      onClick={click}
      onClickCapture={track}
      onAuxClickCapture={track}
    >
      {failed && (
        <Body role="alert">
          Analytics navigation is unavailable. Try again.
        </Body>
      )}
      {children}
    </div>
  );
};
